// Nukes EVERY byte of locally-owned auth / identity / session / chat state on
// an explicit sign-out, so a signed-out browser holds NO auth caches.
//
// `clearAllLocalSessionState({ mode: 'logout' })` tears down the live SDK
// session + clears table contents, but several IndexedDB databases survive as
// (now-empty) shells and a couple of artifacts were missed historically
// (`tokens.access` in settings_kv, the app-side `ping-identity` store). This
// helper runs AFTER that teardown and DELETES every app-created IndexedDB DB
// outright + clears localStorage/sessionStorage. The caller reloads right
// after, so a fresh (empty) DB set is recreated on next boot.
//
// Every step is best-effort and isolated — one failing store must not abort
// the rest of the wipe.

import { openDB } from 'idb';

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';

// The idb-based (non-Dexie) app databases. Their CONTENT must be emptied before
// deletion (see `clearIdbDatabaseContent`) — `deleteDatabase` resolves on
// `onblocked` WITHOUT deleting when the SDK worker or another tab holds them
// open, so without an explicit content-clear a blocked sign-out could leave the
// PREVIOUS account's identity seed (`ping-identity`) + MLS groups
// (`ping-mls-storage`) intact. A different account signing in next would then
// load that stale identity — a cross-user data bleed. `ping-persistence` is
// Dexie and is emptied by STEP 0 below.
const APP_IDB_CONTENT_DATABASES = ['ping-mls-storage', 'ping-identity', 'ping-db'] as const;

/**
 * Empty every object store of an idb database, best-effort, WITHOUT deleting the
 * database shell. Guarantees the sensitive content (identity seed, MLS group
 * state, sync cursors) does not outlive a sign-out even when the subsequent
 * `deleteDatabase` is blocked by a live connection. A clear() transaction does
 * not require exclusive access (unlike a version-change delete), so it succeeds
 * even while the SDK worker still holds the DB open.
 */
async function clearIdbDatabaseContent(name: string): Promise<void> {
  try {
    const database = await openDB(name);
    try {
      const storeNames = Array.from(database.objectStoreNames);
      if (storeNames.length > 0) {
        const tx = database.transaction(storeNames, 'readwrite');
        await Promise.all(storeNames.map((s) => tx.objectStore(s).clear()));
        await tx.done;
      }
    } finally {
      database.close();
    }
  } catch {
    /* best-effort — DB absent, or can't open (e.g. mid-version-change) */
  }
}

// Every IndexedDB database the web app opens (see the investigation in
// docs / clearLocalState.ts). Keep this list exhaustive — a missed DB is an
// auth-cache leak after sign-out.
const APP_IDB_DATABASES = [
  'ping-persistence', // Dexie: chat tables + settings_kv (tokens, device keys, identity row)
  'ping-mls-storage', // SDK MLS state + wrap key
  'ping-identity', // wrapped identity export(s) + wrap keys (two modules, same DB)
  'ping-db', // metadata + sync cursors
] as const;

/**
 * SYNCHRONOUS sweep of the key/value storages + cookies — everything that can
 * be cleared without awaiting. Pulled out so it can run as the VERY LAST step
 * before a hard navigation (`forceCleanReload`), with no `await` after it, so
 * the still-live tab cannot re-write a cleared key (e.g. an Avatar re-rendering
 * and re-populating `ping.img.*`, or `com.ping.convname.*`) in the gap between
 * the wipe and the reload. Safe to call repeatedly (idempotent).
 */
export function clearWebKeyValueStorageSync(): void {
  // localStorage: drop everything Ping owns (prefs, contacts cache, the web
  // preferences store `com.ping.web.preferences`, the `ping.img.*` media cache,
  // `com.ping.convname/pins/saved.*`, notification flags, bootstrap-once…).
  try {
    if (typeof localStorage !== 'undefined') {
      const drop: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== null && (key.startsWith('ping.') || key.startsWith('com.ping.'))) {
          drop.push(key);
        }
      }
      for (const key of drop) localStorage.removeItem(key);
    }
  } catch {
    /* best-effort */
  }

  // sessionStorage: clear our markers (linking-priv, bootstrap-once hints,
  // notification retry counters, the soft-signout flag).
  try {
    if (typeof sessionStorage !== 'undefined') {
      const drop: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key !== null && key.startsWith('ping')) drop.push(key);
      }
      for (const key of drop) sessionStorage.removeItem(key);
    }
  } catch {
    /* best-effort */
  }

  clearOriginCookies();
}

/**
 * Expire every JS-readable cookie on this origin. HttpOnly cookies (e.g. a
 * server-set refresh token) are invisible to JS and are cleared server-side on
 * logout — this handles the rest. We re-issue each deletion across a matrix of
 * path (`/`) and domain scopes (exact host + every parent domain) because a
 * cookie only clears when the expiry write matches the scope it was set with.
 */
function clearOriginCookies(): void {
  try {
    if (typeof document === 'undefined' || typeof document.cookie !== 'string') return;
    const host = typeof location !== 'undefined' ? location.hostname : '';
    // '' = let the browser use the default (host-only) scope; then exact host
    // and each parent suffix (.sub.example.com, .example.com) for shared cookies.
    const domains = new Set<string>(['']);
    if (host.length > 0) {
      domains.add(host);
      const parts = host.split('.');
      for (let i = 0; i < parts.length - 1; i++) domains.add(`.${parts.slice(i).join('.')}`);
    }
    for (const pair of document.cookie.split(';')) {
      const eq = pair.indexOf('=');
      const name = (eq > -1 ? pair.slice(0, eq) : pair).trim();
      if (name.length === 0) continue;
      const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`;
      for (const domain of domains) {
        document.cookie = domain.length === 0
          ? `${expired}; path=/`
          : `${expired}; path=/; domain=${domain}`;
      }
    }
  } catch {
    /* best-effort — cookies blocked / unavailable (private mode, etc.) */
  }
}

function deleteDatabase(name: string): Promise<void> {
  return new Promise<void>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined' || typeof indexedDB.deleteDatabase !== 'function') {
        resolve();
        return;
      }
      const req = indexedDB.deleteDatabase(name);
      // Resolve on any terminal outcome — a `blocked` event means another tab
      // holds the DB open; we don't want to hang the sign-out on it.
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
      // Hard timeout so a stuck request never wedges the reload.
      setTimeout(resolve, 1500);
    } catch {
      resolve();
    }
  });
}

export async function hardWipeBrowserAuthStorage(): Promise<void> {
  // STEP 0 — empty the content FIRST, before any delete. `deleteDatabase`
  // resolves on `onblocked` (another tab / the SDK worker / a still-live
  // liveQuery holds `ping-persistence` open) WITHOUT actually deleting — so the
  // DB and all its rows (tokens, identity, chat tables) survive the "wipe". We
  // therefore explicitly CLEAR every Dexie object store while the handle is
  // still open: even if the subsequent `deleteDatabase` is blocked and the DB
  // shell persists, it persists EMPTY. This is the guarantee that no auth/chat
  // content outlives a sign-out, independent of whether the delete lands.
  try {
    if (!db.isOpen()) {
      await db.open().catch(() => undefined);
    }
    await Promise.all(
      db.tables.map((t) => t.clear().catch(() => undefined)),
    );
  } catch {
    /* best-effort — schema/version error or DB unavailable */
  }

  // Now close the Dexie connection so `deleteDatabase('ping-persistence')`
  // isn't blocked by our OWN open handle (other tabs may still block it, but
  // the content is already gone by STEP 0).
  try {
    db.close();
  } catch {
    /* already closed / not open */
  }

  // STEP 0b — same guarantee for the idb-based stores that STEP 0 gives Dexie:
  // empty the SDK identity seed + MLS groups + cursors BEFORE deleting, so a
  // blocked `deleteDatabase` (SDK worker / other tab still holding a handle)
  // leaves them EMPTY rather than intact. This is the guarantee that the
  // previous account's identity + conversations can NEVER survive a sign-out and
  // bleed into the next account that signs in on this browser.
  await Promise.all(APP_IDB_CONTENT_DATABASES.map((name) => clearIdbDatabaseContent(name)));

  await Promise.all(APP_IDB_DATABASES.map((name) => deleteDatabase(name)));

  // Belt-and-suspenders: enumerate + delete ANY app-created IndexedDB the
  // static list above might have missed (e.g. a future store, or a worker DB).
  // `indexedDB.databases()` is supported on Chromium + recent Firefox; absent
  // on Safari, where the static list is the authority.
  try {
    const enumerable = indexedDB as unknown as {
      databases?: () => Promise<Array<{ name?: string }>>;
    };
    if (typeof enumerable.databases === 'function') {
      const dbs = await enumerable.databases();
      const extra = dbs
        .map((d) => d.name)
        .filter(
          (n): n is string =>
            typeof n === 'string' &&
            (n.startsWith('ping') || n.startsWith('com.ping')) &&
            !(APP_IDB_DATABASES as readonly string[]).includes(n),
        );
      await Promise.all(extra.map((n) => deleteDatabase(n)));
    }
  } catch {
    /* best-effort — enumeration unsupported or blocked */
  }

  // Cache Storage (service-worker / app-shell caches). This is the Ping origin,
  // so every cache here belongs to the app — clearing it removes any cached
  // authenticated responses so the next user can't read the old user's data
  // out of the HTTP cache.
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best-effort — Cache Storage unavailable (private mode, etc.) */
  }

  // Unregister EVERY service worker on this origin. The push SW is currently
  // notification-only (no fetch/cache handler), but unregistering it on an
  // explicit sign-out guarantees no worker survives the post-wipe reload to
  // re-serve anything stale (and a signed-out browser should hold no push
  // subscription anyway — it re-registers on the next sign-in). This is what
  // makes the reload a genuine clean slate: no old UI, code, or cache remains.
  try {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch {
    /* best-effort — Service Worker API unavailable / blocked */
  }

  // localStorage + sessionStorage + cookies — synchronous, shared with the
  // final pre-navigation sweep in `forceCleanReload`.
  clearWebKeyValueStorageSync();

  log.info({ event: 'session.hardWipe.complete' });
}
